import window from 'window-or-global';

// Custom atlassian shortcut key handler
// The default tinymce implementation does not handle left or right key detection for modifier keys (eg ctrl + altLeft + 0 )
const keyCombo = [];

const hasLeftRightVariant = function (key) {
	const variantKeys = [
		'shift',
		'shiftLeft',
		'shiftRight',
		'ctrl',
		'ctrlLeft',
		'ctrlRight',
		'alt',
		'altLeft',
		'altRight',
		'meta',
		'metaLeft',
		'metaRight',
	];
	return variantKeys.indexOf(key) > -1;
};

const enrichModifiers = function (key, keyEvent) {
	return !hasLeftRightVariant(key) ? key : key + (keyEvent.location === 1 ? 'Left' : 'Right');
};

const normaliseKeycode = function (keyCode) {
	// the minus - key can be (189, 45, 109);
	// on Firefox 55.0.3 (64-bit), minus is 173
	// for shortcuts sake, we will call this 189.
	const keys = {
		'-': { default: 189, variants: [189, 45, 109, 173] },
		'\\': { default: 47, variants: [47, 191] },
		'/': { default: 92, variants: [92, 220] },
	};

	for (const i in keys) {
		const candidate = keys[i];
		if (candidate.variants.indexOf(keyCode) > -1) {
			return candidate.default;
		}
	}
	return keyCode;
};

const nomaliseCharCode = function (char) {
	// use this to normalise key definitions from the config
	// eg "-" key can be interpreted as keycode (198, 45, 109)
	const keyCode = char.charCodeAt(0);
	return char === '-' || char === '\\' || char === '/' ? normaliseKeycode(keyCode) : keyCode;
};

const encodeArrowKeys = function (key) {
	const def = {
		arrowleft: 37,
		arrowup: 38,
		arrowright: 39,
		arrowdown: 40,
	};
	return def[key];
};

const processKeys = function (keyEvents) {
	const combo = {};
	keyEvents.forEach((keyEvent) => {
		const keycode = normaliseKeycode(keyEvent.which);
		const key = modifierKeys(keycode);
		const enriched = enrichModifiers(key, keyEvent);
		combo[enriched] = keyEvent;
	});
	return combo;
};

const getMatchedCombo = function (keyCombo, rules) {
	const userKeyCombo = processKeys(keyCombo);
	const matchedShortcut = isShortcut(userKeyCombo, rules);
	return matchedShortcut;
};

const parseConfig = function (conf) {
	const os = window.tinymce.Env.mac ? 'mac' : 'win';
	return conf.map((rule) => {
		const keys = rule[os].split('+').map((key) => {
			if (key.toLowerCase().indexOf('arrow') === 0) {
				return encodeArrowKeys(key.toLowerCase());
			} else {
				return hasLeftRightVariant(key) || key.length > 1 ? key : nomaliseCharCode(key);
			}
		});

		return {
			keys,
			context: rule.context,
			param: rule.param,
			description: rule.description,
		};
	});
};

const modifierKeys = function (keyCode) {
	switch (keyCode) {
		case 16:
			return 'shift';
		case 17:
			return 'ctrl';
		case 18:
			return 'alt';
		case 91:
			return 'meta'; // left meta
		case 93:
			return 'meta'; // right meta
		case 224:
			return 'meta'; // gecko
		default:
			return keyCode;
	}
};

const isModifierKey = function (keyCode) {
	return modifierKeys(keyCode) !== keyCode;
};

const matchVariant = function (combo, key) {
	return !!(combo[`${key}Left`] || combo[`${key}Right`]);
};

const matchAllRules = function (combo, rule) {
	return Object.keys(combo).length !== rule.keys.length
		? false
		: rule.keys.every((key) => {
				return hasLeftRightVariant(key) && matchVariant(combo, key) ? true : !!combo[key];
			});
};

const isShortcut = function (combo, rules) {
	return rules.filter((rule) => {
		return matchAllRules(combo, rule);
	})[0];
};

const triggerShortcut = function (ev, ed, shortcut) {
	if (shortcut) {
		ev.preventDefault();
		const command = shortcut.param.split('-');
		ed.execCommand(command[0], false, command[1]);
	}
};

const clearTypingKeys = function () {
	if (window.tinymce.Env.mac) {
		// when the mac META key is held down,
		// typing keys never fire keyup so is never cleared from keyCombo
		for (let i = 0; i < keyCombo.length; i++) {
			const keycode = keyCombo[i].which;
			if (!isModifierKey(keycode)) {
				keyCombo.splice(i, 1);
				i--;
			}
		}
	}
};

const clearCombo = function (event, keyCombo) {
	for (let i = 0; i < keyCombo.length; i++) {
		const keyEvent = keyCombo[i];
		if (keyEvent && keyEvent.which === event.which) {
			keyCombo.splice(i, 1);
			i--;
		}
	}
};

const resetCombo = function () {
	keyCombo.length = 0;
};

const handleKeydown = function (editor, rules) {
	return function (ev) {
		if (!ev.metaKey && !ev.shiftKey && !ev.altKey && !ev.ctrlKey) {
			resetCombo();
		} else {
			clearCombo(ev, keyCombo);
			keyCombo.push(ev);
			if (keyCombo.length > 1 && getMatchedCombo(keyCombo, rules)) {
				// only handle if more than 1 key was pressed.
				const matchedShortcut = getMatchedCombo(keyCombo, rules);
				triggerShortcut(ev, editor, matchedShortcut);
			}
			clearTypingKeys();
		}
	};
};

const handleKeyup = function () {
	return function (ev) {
		clearCombo(ev, keyCombo);
	};
};

const bindShortcutsToInput = (editor, rules) => {
	const inputs = window.document.querySelectorAll('#versionComment, #content-title');
	if (!inputs || inputs.length === 0) {
		return;
	}
	for (let i = 0; i < inputs.length; i++) {
		rules.forEach((rule) => {
			inputs[i].addEventListener('keydown', (event) => {
				let isMatch = true;

				const code = event.keyCode ? event.keyCode : event.which;
				const metaKey = window.tinymce.Env.mac ? 'meta' : 'ctrl';

				rule.keys.forEach((key) => {
					isMatch = !!(
						isMatch &&
						((key == metaKey && event.metaKey) || (key == 'shift' && event.shiftKey) || code == key)
					);
				});
				if (isMatch) {
					event.preventDefault();
					triggerShortcut(event, editor, rule);
				}
			});
		});
	}
};

const initShortcuts = function (editor, config) {
	const rules = parseConfig(config);
	// !important, the handler must capture keydown first otherwise shortcuts with ENTER will not work.
	editor.on('keydown', handleKeydown(editor, rules), true);
	editor.on('keyup', handleKeyup(), true);

	/**
	 * Work around fixes for when the keyCombo[] can get out of sync due to OS or browser interactions
	 * When a user triggers a native OS or browser shortcut like 'print',
	 * keyup does not fire which means keyCombo[] may go stale,
	 * therefore we resetCombo on blur (os print menu takes focus)
	 */
	editor.on('blur', resetCombo);

	// when a script blocking error occurs, handle keyup may never fire and keyCombo may go out of sync so we reset on error
	window.addEventListener('error', resetCombo);

	// CONFDEV-57456
	bindShortcutsToInput(
		editor,
		rules.filter((rule) => rule.context === 'form.input'),
	);

	return rules;
};

export default initShortcuts;
